/**
 * The reports area's own navigation (ZOUR-22).
 *
 * Mirrors Zoho's Reports Center categories. Items without a `to` render
 * disabled — they are reports we do not serve yet. Adding a report is a `to:`
 * value here plus a route in App.tsx (and, for a Zoho-backed one, a slug in
 * ZOHO_REPORTS below).
 *
 * Copied from the settings rail pattern on purpose rather than sharing its
 * config: the two areas evolve independently.
 */

export interface ReportsNavItem {
  id: string;
  label: string;
  /** Router path. Omitted while the report is not built. */
  to?: string;
  /** Rendered as a plain link out of the area (CFO databook pages). */
  external?: boolean;
}

export interface ReportsNavSection {
  id: string;
  label?: string;
  items: ReportsNavItem[];
}

export const REPORTS_BASE_PATH = "/reports";
export const REPORTS_WORKBENCH_PATH = "/v1";

/** Zoho-backed report slugs; also the `{report}` segment of the API. */
export type ZohoReportSlug = "profit-and-loss" | "balance-sheet" | "cash-flow" | "trial-balance";

export interface ZohoReportDef {
  slug: ZohoReportSlug;
  title: string;
  description: string;
  /** Balance Sheet / Trial Balance are as-of `to_date`. */
  asOf: boolean;
  /** Accrual/Cash toggle. Cash Flow is cash by nature — no toggle. */
  hasBasis: boolean;
  /** Trial balance shows Debit/Credit columns instead of one Total. */
  debitCredit: boolean;
}

export const ZOHO_REPORTS: Record<ZohoReportSlug, ZohoReportDef> = {
  "profit-and-loss": {
    slug: "profit-and-loss",
    title: "Profit and Loss",
    description: "Income, cost of goods sold, expenses and net profit for a period.",
    asOf: false,
    hasBasis: true,
    debitCredit: false,
  },
  "balance-sheet": {
    slug: "balance-sheet",
    title: "Balance Sheet",
    description: "Assets, liabilities and equity as of a date.",
    asOf: true,
    hasBasis: true,
    debitCredit: false,
  },
  "cash-flow": {
    slug: "cash-flow",
    title: "Cash Flow Statement",
    description: "Cash movement from operating, investing and financing activities.",
    asOf: false,
    hasBasis: false,
    debitCredit: false,
  },
  "trial-balance": {
    slug: "trial-balance",
    title: "Trial Balance",
    description: "Net debit and credit balance of every account as of a date.",
    asOf: true,
    hasBasis: true,
    debitCredit: true,
  },
};

export const reportPath = (slug: ZohoReportSlug): string => `${REPORTS_BASE_PATH}/${slug}`;

/**
 * The complete Zoho Reports Center tree (placeholders, accountant, sales,
 * purchases, taxes, CFO databook, Trial Balance). Kept for reference and for
 * when the area grows again; NOT rendered — see REPORTS_NAV_SECTIONS below
 * (follow-up 3, 2026-08-28: the user asked for just the three statements).
 */
export const REPORTS_NAV_SECTIONS_FULL: ReportsNavSection[] = [
  {
    id: "overview",
    items: [{ id: "overview", label: "Overview", to: REPORTS_BASE_PATH }],
  },
  {
    id: "business-overview",
    label: "Business overview",
    items: [
      { id: "profit-and-loss", label: "Profit and Loss", to: reportPath("profit-and-loss") },
      { id: "balance-sheet", label: "Balance Sheet", to: reportPath("balance-sheet") },
      { id: "cash-flow", label: "Cash Flow Statement", to: reportPath("cash-flow") },
      { id: "pl-schedule-iii", label: "P&L (Schedule III)" },
      { id: "horizontal-pl", label: "Horizontal P&L" },
      { id: "horizontal-bs", label: "Horizontal Balance Sheet" },
      { id: "bs-schedule-iii", label: "Balance Sheet (Schedule III)" },
      { id: "ratios", label: "Business Performance Ratios" },
      { id: "movement-of-equity", label: "Movement of Equity" },
    ],
  },
  {
    id: "accountant",
    label: "Accountant",
    items: [
      { id: "trial-balance", label: "Trial Balance", to: reportPath("trial-balance") },
      { id: "general-ledger", label: "General Ledger" },
      { id: "detailed-general-ledger", label: "Detailed General Ledger" },
      { id: "journal-report", label: "Journal Report" },
      { id: "day-book", label: "Day Book" },
      { id: "account-transactions", label: "Account Transactions" },
      { id: "account-type-summary", label: "Account Type Summary" },
    ],
  },
  {
    id: "sales",
    label: "Sales & receivables",
    items: [
      { id: "sales-by-customer", label: "Sales by Customer" },
      { id: "sales-by-item", label: "Sales by Item" },
      { id: "ar-aging-summary", label: "AR Aging Summary" },
      { id: "ar-aging-details", label: "AR Aging Details" },
      { id: "customer-balance-summary", label: "Customer Balance Summary" },
      { id: "invoice-details", label: "Invoice Details" },
    ],
  },
  {
    id: "purchases",
    label: "Purchases & payables",
    items: [
      { id: "purchases-by-vendor", label: "Purchases by Vendor" },
      { id: "ap-aging-summary", label: "AP Aging Summary" },
      { id: "ap-aging-details", label: "AP Aging Details" },
      { id: "vendor-balance-summary", label: "Vendor Balance Summary" },
      { id: "bill-details", label: "Bill Details" },
      { id: "expenses-by-category", label: "Expenses by Category" },
    ],
  },
  {
    id: "taxes",
    label: "Taxes",
    items: [
      { id: "gstr-3b", label: "GSTR-3B Summary" },
      { id: "tax-summary", label: "Tax Summary" },
      { id: "tds-summary", label: "TDS Summary" },
      { id: "tcs-payable-summary", label: "TCS Payable Summary" },
    ],
  },
  {
    id: "cfo-databook",
    label: "CFO databook",
    items: [
      { id: "databook-pl", label: "Profit & Loss", to: "/books/profit-loss", external: true },
      { id: "databook-bs", label: "Balance Sheet", to: "/books/balance-sheet", external: true },
      { id: "databook-cf", label: "Cash Flow", to: "/books/cash-flow", external: true },
    ],
  },
];

/**
 * What the rail and overview actually render: one flat group, three reports.
 * Trial Balance keeps its route/page/API but is not linked from here.
 */
export const REPORTS_NAV_SECTIONS: ReportsNavSection[] = [
  {
    id: "statements",
    items: [
      { id: "profit-and-loss", label: "Profit and Loss", to: reportPath("profit-and-loss") },
      { id: "balance-sheet", label: "Balance Sheet", to: reportPath("balance-sheet") },
      { id: "cash-flow", label: "Cash Flow Statement", to: reportPath("cash-flow") },
    ],
  },
];

export const isReportsNavItemActive = (item: ReportsNavItem, pathname: string): boolean => {
  if (item.to === undefined || item.external) return false;
  if (item.to === REPORTS_BASE_PATH) return pathname === REPORTS_BASE_PATH || pathname === `${REPORTS_BASE_PATH}/`;
  return pathname.startsWith(item.to);
};
